/**
 * @file FetchFromAvitoDialog.tsx
 * @description Модалка «Получить с Avito» (phase14.0.4).
 *
 * Чекбоксы выбора:
 *  - Профиль (GET /accounts/self)
 *  - Баланс (GET /accounts/{id}/balance/)
 *  - Операции (POST /accounts/operations_history/) с селектором периода from/to
 *
 * Один POST /admin/shop/avito/finance/fetch — выполняет всё выбранное за раз.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  shopAvitoFinance,
  type AvitoFinanceFetchInput,
  type AvitoFinanceFetchResultDto,
} from "../../../../lib/api-shop-avito-finance";

interface Props {
  open: boolean;
  onClose: () => void;
  onFetched: (result: AvitoFinanceFetchResultDto) => void;
}

/** Дефолт периода для операций — последние 30 дней. */
function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 3600 * 1000);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

export function FetchFromAvitoDialog({ open, onClose, onFetched }: Props) {
  const notify = useNotify();
  const [profile, setProfile] = useState(true);
  const [balance, setBalance] = useState(true);
  const [operations, setOperations] = useState(true);
  const [range, setRange] = useState(defaultRange);
  const [busy, setBusy] = useState(false);

  async function onSubmit(): Promise<void> {
    if (!profile && !balance && !operations) {
      notify.error("Выберите хотя бы один пункт.");
      return;
    }
    const input: AvitoFinanceFetchInput = {};
    if (profile) input.profile = true;
    if (balance) input.balance = true;
    if (operations) {
      if (!range.from || !range.to) {
        notify.error("Укажите период для операций.");
        return;
      }
      // Полночь UTC от/до — для предсказуемости.
      input.operations = {
        from: new Date(`${range.from}T00:00:00Z`).toISOString(),
        to: new Date(`${range.to}T23:59:59Z`).toISOString(),
      };
    }
    setBusy(true);
    try {
      const result = await shopAvitoFinance.fetch(input);
      notify.success("Данные получены с Avito.");
      onFetched(result);
      onClose();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось получить.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Получить с Avito</DialogTitle>
          <DialogDescription>
            Выберите, что подтянуть. Каждый пункт — отдельный запрос к Avito API.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Checkbox label="Профиль продавца (имя, email, телефон)" checked={profile} onChange={setProfile} />
          <Checkbox label="Баланс (основной кошелёк + бонусы)" checked={balance} onChange={setBalance} />
          <Checkbox label="История операций" checked={operations} onChange={setOperations} />

          {operations && (
            <div className="ml-6 grid grid-cols-2 gap-3 mt-1">
              <div className="flex flex-col gap-1">
                <Label htmlFor="from">С</Label>
                <Input
                  id="from"
                  type="date"
                  value={range.from}
                  onChange={(e) => setRange({ ...range, from: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="to">По</Label>
                <Input
                  id="to"
                  type="date"
                  value={range.to}
                  onChange={(e) => setRange({ ...range, to: e.target.value })}
                />
              </div>
            </div>
          )}
          {operations && (
            <p className="ml-6 text-xs text-on-surface-variant">
              Период больше 30 дней разбивается на чанки автоматически. Максимум — 5 лет.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : "Получить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-outline-variant"
      />
      <span className="text-sm text-on-surface">{label}</span>
    </label>
  );
}
